import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Database, Calendar, Globe, RefreshCw, AlertTriangle, Building2 } from "lucide-react";
import { API } from "../api";
import { formatDate } from "../lib/format";
import { HomeButton } from "./PageChrome";

const ACCENTS = {
  amber: { focus: "focus:border-amber-500", hover: "hover:border-amber-200" },
  red: { focus: "focus:border-red-500", hover: "hover:border-red-200" },
};

// The datasources the caller's organisations own, with search and refresh; reload() refetches.
export function useDatasources() {
  const [datasources, setDatasources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/api/datasources");
      setDatasources(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load datasources.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);
  return { datasources, loading, error, reload };
}

function Detail({ icon: Icon, className = "text-slate-500", children }) {
  return (
    <span className={`flex items-center gap-2 truncate ${className}`}>
      <Icon size={12} className="text-slate-400 flex-shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

// Searchable list of datasources; renderAction draws each row's button.
export function DatasourceList({ list, user, accent, showPublishers = false, renderAction }) {
  const a = ACCENTS[accent] || ACCENTS.amber;
  const { datasources, loading, error, reload } = list;
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return datasources;
    return datasources.filter(d =>
      [d.title, d.endpoint_url, d.datasource_iri].some(v => (v || "").toLowerCase().includes(q)));
  }, [search, datasources]);

  return (
    <>
      <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center mb-8">
        <div className="flex-1 relative">
          <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, endpoint, or IRI..."
            className={`w-full pl-14 pr-6 py-5 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none focus:bg-white font-medium text-slate-700 transition-all ${a.focus}`}
          />
        </div>
        <button
          onClick={reload}
          disabled={loading}
          className="px-8 py-5 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-5 bg-red-50 border border-red-100 rounded-2xl mb-6 flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
          <span className="text-red-700 font-bold text-sm">{error}</span>
        </div>
      )}

      {loading && datasources.length === 0 ? (
        <div className="py-20 text-center text-slate-400 font-medium">Loading datasources...</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center">
          <Database size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500 font-medium">
            {datasources.length === 0 ? (
              <>
                No datasources owned by{" "}
                <span className="font-black text-green-600">
                  {(user?.orgs || []).map((o) => o.name).join(", ") || "your organisation"}
                </span>{" "}
                have been registered yet.
              </>
            ) : "No matches for your search."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((ds) => (
            <motion.div
              key={ds.datasource_iri}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`group bg-slate-50 hover:bg-white border-2 border-slate-100 rounded-3xl p-6 transition-all ${a.hover}`}
            >
              <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-black text-lg text-slate-900 tracking-tight mb-2 truncate">{ds.title || "Untitled"}</h3>
                  <div className="flex flex-col gap-1 text-xs">
                    <Detail icon={Globe} className="font-mono text-slate-600">{ds.endpoint_url}</Detail>
                    <Detail icon={Database} className="font-mono text-slate-500">{ds.datasource_iri}</Detail>
                    <Detail icon={Calendar}>Modified: {formatDate(ds.modified)}</Detail>
                    {showPublishers && (
                      <Detail icon={Building2}>
                        Publisher: {ds.publishers?.length ? ds.publishers.join(", ") : "Not recorded"}
                      </Detail>
                    )}
                  </div>
                </div>
                {renderAction(ds)}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </>
  );
}

// Page body shared by Modify and Deregister: home button, heading, then the list.
export function DatasourcePage({ onBack, title, intro, children }) {
  return (
    <>
      {onBack && <div className="-mt-2 -ml-2 mb-6"><HomeButton onClick={onBack} /></div>}
      <header className="mb-10 text-left">
        <h1 className="text-2xl font-black uppercase text-slate-900 tracking-widest mb-4">{title}</h1>
        <p className="text-gray-800 font-medium text-lg leading-relaxed">{intro}</p>
      </header>
      {children}
    </>
  );
}
